import * as React from 'react';
import { useCallback, useState } from 'react';

import type { InjectedRouter } from 'js/lib/connectToRouter';

import CourseEnrollModal from 'bundles/enroll-modal/components/CourseEnrollModal';
import type { PropsFromCaller as CourseEnrollModalProps } from 'bundles/enroll-modal/components/CourseEnrollModal';
import S12nEnrollModal from 'bundles/enroll-modal/components/S12nEnrollModal';
import type { PropsFromCaller as S12nEnrollModalProps } from 'bundles/enroll-modal/components/S12nEnrollModal';
import PageDataProvider from 'bundles/enroll-modal/data/PageDataProvider';

export type PropsFromCaller =
  | S12nEnrollModalProps
  | (Omit<
      CourseEnrollModalProps,
      | 'onS12nSelect'
      | 'onS12nChange'
      | 'onS12nChangeCancel'
      | 'hasChangedS12n'
      | 'previousS12nId'
      | 'isMixAndMatchOrderingExperiment'
    > & { router?: InjectedRouter });

const isS12nProps = (props: PropsFromCaller): props is S12nEnrollModalProps =>
  Boolean(props.s12nId && (props as S12nEnrollModalProps).onSdp);

const isCourseProps = (props: PropsFromCaller): props is CourseEnrollModalProps =>
  Boolean(props.courseId && !(props as S12nEnrollModalProps).onSdp);

const EnrollModal = (props: PropsFromCaller) => {
  // For Mix and Match, pass the selected s12n back to the provider
  const [selectedS12nId, setSelectedS12nId] = useState<string | undefined>();
  // Lives here rather than in CourseEnrollModal, which remounts while the s12n selection data reloads
  const [hasChangedS12n, setHasChangedS12n] = useState(false);
  // The optimize-mix-and-match experiment picked the s12n for the learner; also lost if CourseEnrollModal remounts
  const [isMixAndMatchOrderingExperiment, setIsMixAndMatchOrderingExperiment] = useState(false);
  // The s12n the learner left via "Change program", restored if they close the selection modal
  const [previousS12nId, setPreviousS12nId] = useState<string | undefined>();
  const handleS12nSelect = useCallback((s12nId: string, { isMixAndMatchOrderingSkip = false } = {}) => {
    setSelectedS12nId(s12nId);
    setPreviousS12nId(undefined);
    if (isMixAndMatchOrderingSkip) {
      setIsMixAndMatchOrderingExperiment(true);
    }
  }, []);
  let childComponent;

  // TODO remove passing props to child components once all data is in provider
  if (isS12nProps(props)) {
    childComponent = <S12nEnrollModal {...props} />;
  } else if (isCourseProps(props)) {
    childComponent = (
      <CourseEnrollModal
        {...props}
        onS12nSelect={handleS12nSelect}
        isMixAndMatchOrderingExperiment={isMixAndMatchOrderingExperiment}
        hasChangedS12n={hasChangedS12n}
        previousS12nId={previousS12nId}
        onS12nChange={() => {
          setHasChangedS12n(true);
          setPreviousS12nId(selectedS12nId);
          setSelectedS12nId(undefined);
        }}
        onS12nChangeCancel={() => {
          setSelectedS12nId(previousS12nId);
          setPreviousS12nId(undefined);
        }}
      />
    );
  } else {
    throw new Error('Cannot render EnrollModal with invalid props');
  }

  return (
    <PageDataProvider {...props} selectedS12nId={selectedS12nId}>
      {childComponent}
    </PageDataProvider>
  );
};

export default EnrollModal;
